import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysSupplier } from '../queryKeys';

import { fetchNetworkSuppliers } from './fetchNetworkSuppliers';
import {
  type NetworkSuppliersQueryParams,
  type NetworkSuppliersResponse,
} from './types';

/**
 * Generates the query options for the network suppliers query so they can be
 * shared between the useQuery hook and prefetching in route loaders.
 *
 * @param queryParams - Filter and pagination parameters.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getNetworkSuppliersQueryOptions = <
  TData = NetworkSuppliersResponse,
>({
  queryParams,
  options,
}: {
  queryParams: Partial<NetworkSuppliersQueryParams>;
  options?: Omit<
    UseQueryOptions<NetworkSuppliersResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: queryParams.recipientCompanyIds?.length
      ? async ({ signal }: QueryFunctionContext) =>
          fetchNetworkSuppliers(queryParams, { signal })
      : skipToken,
    queryKey: queryKeysSupplier.getNetwork(queryParams),
    staleTime: 0,
    throwOnError: false,
    ...options,
  });

/**
 * React Query hook for fetching the paginated network catalog of visible suppliers.
 *
 * @param queryParams - Filter and pagination parameters.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook containing the network suppliers page.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_network_suppliers_suppliers_network_get
 */
export const useQueryNetworkSuppliers = <TData = NetworkSuppliersResponse>(
  queryParams: Partial<NetworkSuppliersQueryParams>,
  options?: Omit<
    UseQueryOptions<NetworkSuppliersResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getNetworkSuppliersQueryOptions<TData>({ queryParams, options }));
